import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase
          .from('user_profiles')
          .select(`*`)
          .eq('id', userId)
          .single();

      if (error) throw error;
      setProfile((prev) => {
        if (prev && data && prev.id === data.id && prev.updated_at === data.updated_at) {
          return prev;
        }
        return data;
      });
      setError(null);
      return data;
    } catch (err) {
      setError(err.message);
      return null;
    }
  };

  const refreshProfile = async (userId) => {
    const id = userId || user?.id;
    if (!id) return null;
    return loadUserProfile(id);
  };

  useEffect(() => {
    setLoading(true);
    const getSessionAndSubscribe = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) throw error;

        if (session?.user) {
          setUser(session.user);
          await loadUserProfile(session.user.id);
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch (err) {
        setError(err.message);
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((event, session) => {
        // Ne pas recharger l'UI à chaque refresh de token (sinon "actualisation" permanente)
        if (event === 'TOKEN_REFRESHED') {
          if (session?.user) {
            setUser((prev) => (prev?.id === session.user.id ? prev : session.user));
          }
          return;
        }

        setLoading(false);
        if (session?.user) {
          setUser(session.user);
          if (event === 'SIGNED_IN' || event === 'USER_UPDATED' || event === 'INITIAL_SESSION') {
            loadUserProfile(session.user.id);
          }
        } else {
          setUser(null);
          setProfile(null);
        }
      });

      return () => subscription.unsubscribe();
    };

    getSessionAndSubscribe();
  }, []);

  const signIn = async (email, password) => {

    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data?.user) {

        await supabase
            .from('user_profiles')
            .update({ last_login_at: new Date().toISOString() })
            .eq('id', data.user.id);

      }
      setUser(data.user);

      await loadUserProfile(data.user.id);
      return { user: data.user, error: null };
    } catch (err) {
      setError(err.message);
      return { user: null, error: err };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    try {
      sessionStorage.setItem('postLogoutRedirect', '/');

      const { error } = await supabase.auth.signOut();

      if (error) throw error;
      setUser(null);
      setProfile(null);
      setError(null);
      return { error: null };
    } catch (err) {
      sessionStorage.removeItem('postLogoutRedirect');
      setError(err.message);
      return { error: err };
    } finally {
      setLoading(false);
    }
  };

  return (
      <AuthContext.Provider value={{ user, profile, loading, error, signIn, signOut, refreshProfile }}>
        {children}
      </AuthContext.Provider>
  );
};
